import localforage from 'localforage';

/**
 * Redux Persist가 로딩하기 전에 IndexedDB 데이터를 미리 확인
 * @returns {Promise<Object|null>} { multiModelData, totalEntities, totalAttrs, modelCount, undoCount, redoCount } 또는 null
 */
export const previewIndexedDBData = async () => {
  try {
    const storage = localforage.createInstance({
      name: 'datamodeler-db',
      storeName: 'redux-persist',
      driver: [
        localforage.INDEXEDDB,
        localforage.WEBSQL,
        localforage.LOCALSTORAGE
      ], // Fallback 순서
      description: 'Data Modeler Application Storage'
    });

    // Storage 준비 대기 (에러 발생 시 null 반환)
    await storage.ready().catch(() => {
      console.warn('Storage not available, skipping preview');
      return null;
    });

    // Redux Persist 키에서 데이터 가져오기
    const persistData = await storage.getItem('persist:datamodeler');

    if (!persistData) {
      return null;
    }

    // JSON 파싱
    const parsed = JSON.parse(persistData);
    const baseData = JSON.parse(parsed.base || '{}');
    const rawMultiModelData = baseData.common?.multiModelData;

    // multiModelData가 객체(Record)인 경우 배열로 변환
    const multiModelData = Array.isArray(rawMultiModelData)
      ? rawMultiModelData
      : Object.values(rawMultiModelData || {});

    if (multiModelData.length === 0) {
      return null;
    }

    // 엔터티 수 계산 (memoNode 제외)
    const totalEntities = multiModelData.reduce((sum, model) => {
      return sum + (model.nodes?.filter(n => n.type !== 'memoNode' && n.type !== 'memo').length || 0);
    }, 0);

    // 속성 수 계산
    const totalAttrs = multiModelData.reduce((sum, model) => {
      return sum + (model.attrs || []).reduce((aSum, attrGroup) => {
        return aSum + (attrGroup.items?.length || 0);
      }, 0);
    }, 0);

    // undo/redo 히스토리 개수 조회 (ModelHistoryDB)
    let undoCount = 0;
    let redoCount = 0;
    try {
      const historyData = await getHistoryPreview();
      undoCount = historyData.undoCount;
      redoCount = historyData.redoCount;
    } catch (e) {
      // 히스토리 DB 접근 실패해도 무시
    }


    return {
      multiModelData,
      totalEntities,
      totalAttrs,
      modelCount: multiModelData.length,
      undoCount,
      redoCount,
    };
  } catch (error) {
    console.error('IndexedDB 미리보기 실패:', error);
    return null;
  }
};

/**
 * ModelHistoryDB에서 undo/redo 히스토리 총 개수를 미리 조회
 */
const getHistoryPreview = () => {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve({ undoCount: 0, redoCount: 0 });
      return;
    }
    const request = indexedDB.open('ModelHistoryDB', 1);
    request.onerror = () => resolve({ undoCount: 0, redoCount: 0 });
    request.onsuccess = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('histories')) {
        resolve({ undoCount: 0, redoCount: 0 });
        return;
      }
      const tx = db.transaction(['histories'], 'readonly');
      const store = tx.objectStore('histories');
      const getAll = store.getAll();
      getAll.onsuccess = () => {
        const records = getAll.result || [];
        let undoCount = 0;
        let redoCount = 0;
        records.forEach((record) => {
          undoCount += record.history?.past?.length || 0;
          redoCount += record.history?.future?.length || 0;
        });
        resolve({ undoCount, redoCount });
      };
      getAll.onerror = () => resolve({ undoCount: 0, redoCount: 0 });
    };
  });
};

/**
 * IndexedDB에서 특정 키의 데이터 삭제
 * @param {string} key - 삭제할 키 (예: 'persist:datamodeler')
 */
export const clearIndexedDBKey = async (key = 'persist:datamodeler') => {
  try {
    const storage = localforage.createInstance({
      name: 'datamodeler-db',
      storeName: 'redux-persist',
      driver: [
        localforage.INDEXEDDB,
        localforage.WEBSQL,
        localforage.LOCALSTORAGE
      ]
    });

    await storage.ready().catch(() => null);
    await storage.removeItem(key);
    return true;
  } catch (error) {
    console.error('IndexedDB 키 삭제 실패:', error);
    return false;
  }
};

/**
 * IndexedDB에서 base.common.multiModelData만 제거하고 auth/locale는 유지
 */
export const clearBaseDataOnly = async () => {
  try {
    const storage = localforage.createInstance({
      name: 'datamodeler-db',
      storeName: 'redux-persist',
      driver: [
        localforage.INDEXEDDB,
        localforage.WEBSQL,
        localforage.LOCALSTORAGE
      ]
    });

    await storage.ready().catch(() => null);
    const persistData = await storage.getItem('persist:datamodeler');
    if (!persistData) {
      return true;
    }

    // JSON 파싱
    const parsed = JSON.parse(persistData);
    const baseData = JSON.parse(parsed.base || '{}');

    // base.common.multiModelData만 초기화
    if (baseData.common) {
      baseData.common.multiModelData = [];
      baseData.common.modelInfo = null;
      baseData.common.nodes = [];
      baseData.common.edges = [];
      baseData.common.attrs = [];
    }

    // 수정된 base 데이터를 다시 저장
    parsed.base = JSON.stringify(baseData);
    await storage.setItem('persist:datamodeler', JSON.stringify(parsed));

    return true;
  } catch (error) {
    console.error('Base 데이터 초기화 실패:', error);
    return false;
  }
};
